import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from '@mui/material';

import { zodResolver } from '@hookform/resolvers/zod';
import 'dayjs/locale/nl';
import { useForm } from 'react-hook-form';
import { usePlusminApi } from '../../api/plusminApi';
import { useCustomContext } from '../../context/CustomContext';
import { BetalingDTO, BetalingsSoort } from '../../model/Betaling';
import { SaldoDTO, Stand } from '../../model/Saldo';
import {
  defaultFormSaldos,
  formSchema,
  FormValues,
  formatAmount,
} from '../Potjes/HevelReserveOverFormUtil';
import {
  RekeningGroepSoort,
  reserverenRekeningGroepSoorten,
} from '../../model/RekeningGroep';
import dayjs from 'dayjs';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

interface HevelReserveOverFormProps {
  stand: Stand;
  saldo: SaldoDTO;
  onHevelReserveOverClose: () => void;
}

export function HevelReserveOverForm({
  stand,
  saldo,
  onHevelReserveOverClose,
}: HevelReserveOverFormProps) {
  const { actieveAdministratie, setSnackbarMessage, setIsStandDirty } =
    useCustomContext();
  const { postBetalingVooradministratie } = usePlusminApi();
  const [open, setOpen] = useState<boolean>(true);

  const selecteerbareBonnen = stand.resultaatOpDatum
    .filter((item) =>
      reserverenRekeningGroepSoorten.includes(
        item.rekeningGroepSoort as RekeningGroepSoort,
      ),
    )
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      formReservering: {
        ...defaultFormSaldos(
          stand.resultaatOpDatum.find(
            (item) => item.rekeningNaam === saldo.rekeningNaam,
          ) ?? stand.resultaatOpDatum[0],
        ),
        bron: selecteerbareBonnen[0]?.rekeningNaam || '',
      },
    },
  });

  const watchedBedrag = watch('formReservering.bedrag');
  const formReservering = getValues('formReservering');
  const bron = formReservering.bron.replace(/,/g, '.');
  if (getValues(`formReservering.bron`) !== bron) {
    setValue(`formReservering.bron`, bron);
  }

  const onSubmit = (data: FormValues) => {
    saveWijzigingen(data.formReservering);
    handleClose();
  };

  const bepaalPotjeSoort = (rekeningNaam: string) => {
    const gevondenSaldo = stand.resultaatOpDatum.find(
      (item) => item.rekeningNaam === rekeningNaam,
    )!;
    console.log(
      'Gevonden saldo voor rekeningNaam',
      rekeningNaam,
      gevondenSaldo.rekeningGroepSoort,
    );
    return gevondenSaldo.rekeningGroepSoort === RekeningGroepSoort.spaarpot
      ? 'SP'
      : 'P';
  };

  const saveWijzigingen = async (
    formReservering: FormValues['formReservering'],
  ) => {
    if (actieveAdministratie) {
      const betalingsSoort = BetalingsSoort.reserveren;
      const waarschuwing = bepaalPotjeSoort(formReservering.bron) !== bepaalPotjeSoort(formReservering.bestemming);
      console.log('Saving wijzigingen', formReservering, betalingsSoort);
      try {
        await postBetalingVooradministratie(actieveAdministratie, {
          ...(formReservering as unknown as BetalingDTO),
          betalingsSoort: betalingsSoort as BetalingsSoort,
          bedrag: Number(formReservering.bedrag),
          boekingsdatum: stand.periodeStartDatum ?? dayjs().toISOString().substring(0, 10),
          omschrijving:
            formReservering.omschrijving &&
            formReservering.omschrijving?.length > 0
              ? formReservering.omschrijving
              : `Hevel reserve over van ${formReservering.bron} naar ${formReservering.bestemming}`,
        });
        setSnackbarMessage({
          message: `De reserve is succesvol overgeheveld.` + (waarschuwing ? ' Let op: er is geld geboekt tussen spaar- en betaalrekening' : ''),
          type: 'success',
        });
        setIsStandDirty(true);
      } catch (error) {
        console.error('Error saving wijzigingen', error);
        setSnackbarMessage({
          message: `De configuratie voor ${actieveAdministratie!.naam} is niet correct.`,
          type: 'warning',
        });
      }
    }
  };

  const handleClose = () => {
    onHevelReserveOverClose();
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Hevel reserve over naar {formReservering.bestemming}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TableContainer sx={{ mr: 'auto', my: '10px' }}>
              <Table sx={{ width: '100%' }} aria-label="simple table">
                <TableBody>
                  <TableRow sx={{ '& td, & th': { border: 0 } }}>
                    <TableCell
                      align="left"
                      size="small"
                      sx={{ fontWeight: '500' }}
                    >
                      Bedrag
                    </TableCell>
                    <TableCell
                      align="left"
                      size="small"
                      sx={{ fontWeight: '500' }}
                    >
                      <TextField
                        sx={{ width: '33%' }}
                        variant="standard"
                        id={`formReservering.bedrag`}
                        type="text"
                        inputRef={register(`formReservering.bedrag`).ref}
                        {...register(`formReservering.bedrag`)}
                        slotProps={{
                          input: {
                            style: {
                              textAlign: 'right',
                              fontSize: '0.9rem',
                            },
                            startAdornment: (
                              <InputAdornment position="start">
                                €
                              </InputAdornment>
                            ),
                          },
                        }}
                        error={errors.formReservering?.bedrag ? true : false}
                        helperText={errors.formReservering?.bedrag?.message}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ '& td, & th': { border: 0 } }}>
                    <TableCell
                      size="small"
                      sx={{ fontWeight: '500', fontSize: '0.85rem' }}
                    >
                      Bron
                    </TableCell>
                    <TableCell
                      size="small"
                      sx={{ fontWeight: '500', fontSize: '0.85rem' }}
                    >
                      <FormControl
                        variant="standard"
                        sx={{ minWidth: 200 }}
                        error={errors.formReservering?.bron ? true : false}
                      >
                        <InputLabel id="bron-select-label">
                          Selecteer bron
                        </InputLabel>
                        <Select
                          labelId="bron-select-label"
                          id="formReservering.bron"
                          sx={{ fontSize: '0.85rem' }}
                          {...register('formReservering.bron')}
                          value={watch('formReservering.bron') || ''}
                          onChange={(event) => {
                            setValue(
                              'formReservering.bron',
                              event.target.value,
                            );
                          }}
                        >
                          {selecteerbareBonnen
                            .filter(
                              (saldo) =>
                                saldo.rekeningNaam !==
                                formReservering.bestemming,
                            )
                            .map((saldo) => {
                              const beschikbareBedrag =
                                saldo.openingsReserveSaldo +
                                saldo.periodeReservering -
                                saldo.periodeBetaling;
                              const gewenstBedrag = Number(watchedBedrag) || 0;

                              return (
                                <MenuItem
                                  key={saldo.rekeningNaam}
                                  value={saldo.rekeningNaam}
                                  sx={{ fontSize: '0.85rem' }}
                                  disabled={
                                    beschikbareBedrag <= 0 ||
                                    beschikbareBedrag < gewenstBedrag
                                  }
                                >
                                  {saldo.rekeningNaam} (
                                  {formatAmount(beschikbareBedrag)})
                                </MenuItem>
                              );
                            })}
                        </Select>
                        {errors.formReservering?.bron && (
                          <FormHelperText>
                            {errors.formReservering.bron.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ '& td, & th': { border: 0 } }}>
                    <TableCell
                      size="small"
                      sx={{ fontWeight: '500', fontSize: '0.85rem' }}
                    >
                      Omschrijving
                    </TableCell>
                    <TableCell
                      size="small"
                      sx={{ fontWeight: '500', fontSize: '0.85rem' }}
                    >
                      <TextField
                        sx={{
                          width: '100%',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                        }}
                        variant="standard"
                        id={`formReservering.omschrijving`}
                        type="text"
                        multiline
                        minRows={1}
                        maxRows={4}
                        placeholder="Optioneel: extra toelichting"
                        inputRef={register(`formReservering.omschrijving`).ref}
                        {...register(`formReservering.omschrijving`)}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            sx={{ color: 'success.main' }}
            startIcon={
              <SaveOutlinedIcon
                sx={{ fontSize: '35px', color: 'success.main' }}
              />
            }
            type="submit"
          >
            BEWAAR
          </Button>
        </DialogActions>
      </form>
    </BootstrapDialog>
  );
}
